
import { AdminLayout } from "@/components/layout/AdminLayout";
import Login from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Content from "@/pages/admin/Content";
import Redirects from "@/pages/admin/Redirects";
import BlogManager from "@/pages/admin/BlogManager";
import EditBlogPost from "@/pages/admin/EditBlogPost";
import WordPressImport from "@/pages/admin/WordPressImport";
import BrandBook from "@/pages/admin/BrandBook";
import UserManagement from "@/pages/admin/UserManagement";
import ProfileSettings from "@/pages/admin/ProfileSettings";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import { clearRedirectCache } from "@/utils/redirectUtils";

const ProtectedRoute = ({ element }: { element: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    // Log the current path for debugging
    console.log("Protected route accessed:", location.pathname);
    console.log("Auth state:", user ? "Authenticated" : "Unauthenticated", "Loading:", loading);
    
    // Clear redirect cache for admin routes to prevent redirects interfering with protected routes
    clearRedirectCache();
  }, [location.pathname, user, loading]);
  
  // If we're still loading, render a loading indicator
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-16 h-16 border-4 border-edgefi-purple/30 border-t-edgefi-purple rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login and include the return path
  if (!user) {
    console.log("User not authenticated, redirecting to login");
    // Get the current path to redirect back after login
    const currentPath = window.location.pathname;
    // Remove trailing slash if present for consistency
    const normalizedPath = currentPath.endsWith('/') && currentPath !== '/' 
      ? currentPath.slice(0, -1) 
      : currentPath;
    return <Navigate to={`/admin/login?returnTo=${encodeURIComponent(normalizedPath)}`} replace />;
  }
  
  // If authenticated, render the protected element
  return <>{element}</>;
};

// Wrapper for AdminLayout that applies protection
const ProtectedAdminLayout = () => {
  // Clear redirect cache when admin layout mounts
  useEffect(() => {
    clearRedirectCache();
  }, []);
  
  return (
    <ProtectedRoute 
      element={
        <AdminLayout>
          <Outlet />
        </AdminLayout>
      } 
    />
  );
};

// Helper function to normalize paths by removing trailing slashes
const createRedirectRoute = (from: string, to: string) => ({
  path: from,
  element: <Navigate to={to} replace />
});

export const adminRoutes = {
  path: "/admin",
  children: [
    // Login route is NOT protected - it should be accessible without authentication
    { path: "login", element: <Login /> },
    
    // All other admin routes are protected and use the AdminLayout
    {
      element: <ProtectedAdminLayout />,
      children: [
        { path: "", element: <Navigate to="/admin/dashboard" replace /> },
        { path: "dashboard", element: <Dashboard /> },
        // Handle trailing slash for dashboard
        createRedirectRoute("dashboard/", "dashboard"),
        { path: "content", element: <Content /> },
        // Handle trailing slash for content
        createRedirectRoute("content/", "content"),
        { path: "redirects", element: <Redirects /> },
        // Handle trailing slash for redirects
        createRedirectRoute("redirects/", "redirects"),
        { path: "blog", element: <BlogManager /> },
        // Handle trailing slash for blog
        createRedirectRoute("blog/", "blog"),
        { path: "blog/:slug", element: <EditBlogPost /> },
        { path: "wordpress-import", element: <WordPressImport /> },
        // Handle trailing slash for wordpress-import
        createRedirectRoute("wordpress-import/", "wordpress-import"),
        { path: "brand-book", element: <BrandBook /> },
        // Handle trailing slash for brand-book
        createRedirectRoute("brand-book/", "brand-book"),
        { path: "users", element: <UserManagement /> },
        // Handle trailing slash for users
        createRedirectRoute("users/", "users"),
        { path: "profile", element: <ProfileSettings /> },
        // Handle trailing slash for profile
        createRedirectRoute("profile/", "profile"),
      ]
    }
  ]
};
